import { INVESTOR_PROFILE_QUERY_KEY, useGetProfileInvestor } from "api/user/queries";
import { useCallback, useEffect, useState } from "react";
import { useQueryCache } from "react-query";
import { createContainer } from "unstated-next";
import { useAuthPermissions } from "hooks/useAuthUser";
import { PERMISSIONS } from "utils/constant";

function useRiskDeclaration() {
	const queryCache = useQueryCache();

	const [isOpen, setIsOpen] = useState(false);

	const [isSubmitted, setIsSubmitted] = useState<boolean | undefined>(undefined);

	const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
	const permissions = useAuthPermissions();

	const isOmnibusUser = useCallback((): boolean => {
		return permissions.includes(PERMISSIONS.WEB_OMNIBUS_AUTHORIZE);
	}, [permissions]);

	const { data: profile, refetch: refetchProfile } = useGetProfileInvestor(undefined, {
		enabled: isOmnibusUser(),
	});

	useEffect(() => {
		if (!isOmnibusUser()) return;
		if (profile === undefined) return;

		setIsSubmitted(Boolean((profile as any)?.riskDeclaration?.submitted));
	}, [profile, isOmnibusUser]);

	const open = useCallback(() => setIsOpen(true), []);

	const close = useCallback(() => setIsOpen(false), []);

	const guardAction = useCallback(
		async (action?: () => void): Promise<boolean> => {
			if (!isOmnibusUser()) {
				action?.();
				return true;
			}

			let submitted = isSubmitted;
			if (submitted === undefined) {
				const data = await refetchProfile();
				submitted = Boolean((data as any)?.riskDeclaration?.submitted);
				setIsSubmitted(submitted);
			}

			if (submitted === true) {
				action?.();
				return true;
			}

			if (action) {
				setPendingAction(() => action);
			}

			setIsOpen(true);

			return false;
		},
		[isOmnibusUser, isSubmitted, refetchProfile],
	);

	const markSubmitted = useCallback(() => {
		setIsSubmitted(true);

		setIsOpen(false);

		queryCache.invalidateQueries([INVESTOR_PROFILE_QUERY_KEY]);

		if (pendingAction) {
			pendingAction();
			setPendingAction(null);
		}
	}, [pendingAction, queryCache]);

	return {
		isOpen,
		isSubmitted,
		open,
		close,
		markSubmitted,
		guardAction,
		isOmnibusUser,
	};
}

export const RiskDeclaration = createContainer(useRiskDeclaration);
